/**
 * View types for the audiences domain.
 *
 * The shapes the BACKEND owns are not re-declared here — they are re-exported from the contract
 * (`@server/audience-schema` for the stored document, `@server/audience-rows` for the derived `Row`),
 * so a field added on the backend arrives here without being copied. Both imports are TYPE-ONLY and
 * erased at build time: `expression-schema.ts` pulls zod and `rows.ts` pulls the sketch/fold helpers,
 * and nothing in this file needs a value from either, so the SPA bundle is unchanged.
 *
 * There is no wire/domain gap to model. A wire type earns its place when what travels differs from
 * what is stored — a `Date` that serialises to a string, a redacted projection, or a consumer on its
 * own deploy cycle. None apply: `AudienceRow` already types every timestamp as `string`, `Row` is sent
 * whole, and the oRPC client is generated from this same repo's router.
 *
 * What this file declares itself is only what the contract has no counterpart for: the count/list view
 * models below, derived per render and never stored.
 *
 * Components import the domain's types from HERE rather than reaching for `@server/*` directly, so the
 * surface the frontend actually consumes stays readable in one place.
 */

import type { AccountRepairListState } from '@server/warehouse-repair-state';

export type {
  AttributePredicate,
  AudienceDefinition,
  AudienceExpression,
  Predicate,
  RawSqlPredicate,
} from '@server/audience-schema';

/** The derived grounding lookup `audience.get` returns beside the audience. Never stored — recomputed
 *  on every read from the expression plus the audience's schema sketch. */
export type { Row as AudienceRowWire, RowControl as RowControlWire } from '@server/audience-rows';

/** The predicate-tree walk, shared with the contract and the builder. See `@server/audience-tree`. */
export {
  andChildrenOf,
  attributeOf,
  listLeaves,
  orChildrenOf,
  rawSqlOf,
  topLevelConjuncts,
} from '@server/audience-tree';

// ---- count state (a rendering view model — NOT `CountResult` from exec.ts, which also carries the
// display SQL and isn't relevant to how these components render a count) ----

export type CountState =
  | { status: 'not-run' }
  | { status: 'counted'; members: number; universe: number; percentage: number }
  | { status: 'error'; error: string };

/**
 * Save is enabled only for a fresh, successful count of the CURRENT definition: never for `not-run`
 * (nothing is known — never rendered as 0), never for `error` (the last attempt failed), and never
 * while `stale` (the definition changed since the count that produced `state`). Mirrors
 * minimum-artifact §6.2 / product-scope §2's "Save enabled only with a current-hash count".
 */
export function canSaveAudience(state: CountState, stale: boolean): boolean {
  return state.status === 'counted' && !stale;
}

// ---- list-page view model ----

export interface AudienceListItem {
  id: string;
  title: string;
  status: 'draft' | 'saved';
  /** Count of top-level predicate conjuncts — the audience's "conditions". */
  ruleCount: number;
  /** Warehouse account name. Empty while the warehouse list is loading, or when the warehouse is gone. */
  warehouseLabel: string;
  /**
   * Backend warehouse `type` (e.g. `POSTGRES`, `SNOWFLAKE`) for the list's type logo. Empty when the
   * warehouse is missing from `settings.warehouses.list` — the mark falls back to the generic icon.
   */
  warehouseType: string;
  /**
   * Lookout-recorded activations for this audience (`audience_activations` count on `audience.list`).
   * Zero means none — the list hides the chip rather than showing "0 activations".
   */
  activationCount: number;
  /** Creator user id — null for system-created rows. Gates the list's per-row Delete action. */
  createdBy: string | null;
  /**
   * The RudderStack rETL source the audience syncs to; null while it never synced. In a sibling workspace a synced
   * audience's Delete is RudderStack's to decide (ACT2-1124 LLD §6), so the list's gate reads it.
   */
  rsSourceId: string | null;
  /** ISO timestamp. */
  updatedAt: string;
  countState: CountState;
  /** The definition changed since `countState` was produced. Meaningless (and ignored by callers)
   * when `countState.status === 'not-run'` — there is no prior count to be stale against. */
  stale: boolean;
  /** Needs-repair status (ACT2-1286); `ok`, or absent, shows nothing. Always `ok` with the flag off. */
  repairState?: AccountRepairListState['state'];
}
